/**
 * AttributeRuleEngine
 *
 * Core business logic for evaluating attribute rules based on context
 */

import type { ApolloClient } from "@apollo/client"
import type { AttributeConfig, AttributeRuleMap, ActivityContext, ValidationResult } from "./types"
import { DateTime } from "timezonecomplete"
import { toTimestamp, toUserTZ } from "@hydrologiq/common/helpers/date"
import { AssetBasic } from "@hydrologiq/gql/graphql"

/**
 * Engine for evaluating attribute rules
 *
 * Provides methods to:
 * - Get visible attributes for a given activity/status
 * - Calculate default values from context
 * - Validate user input
 * - Fetch IoT sensor data
 */
export class AttributeRuleEngine {
  private ruleMap: AttributeRuleMap

  /**
   * Create a new rule engine with the given rule map
   */
  constructor(ruleMap: AttributeRuleMap) {
    this.ruleMap = ruleMap
  }

  /**
   * Get all visible attributes for a given activity type and status
   *
   * Filters out hidden attributes and returns configs for:
   * - visible
   * - iot
   * - calculated
   *
   * @param activityKind - Type of activity (filling, delivery, collection)
   * @param eventKind - Current status (scheduled, started, completed)
   * @returns Array of attribute configs that should be displayed
   */
  getVisibleAttributes(activityKind: string, eventKind: string): AttributeConfig[] {
    const configs =
      this.ruleMap[activityKind as keyof AttributeRuleMap]?.[
      eventKind as keyof AttributeRuleMap[keyof AttributeRuleMap]
      ] || []
    return configs.filter((c: AttributeConfig) => c.visibility !== "hidden")
  }

  /**
   * Get ALL attributes for a given activity type and status (including hidden)
   *
   * Used for processing hidden attributes that need to be saved but not shown
   *
   * @param activityKind - Type of activity (filling, delivery, collection)
   * @param eventKind - Current status (scheduled, started, completed)
   * @returns Array of all attribute configs
   */
  getAllAttributes(activityKind: string, eventKind: string): AttributeConfig[] {
    return (
      this.ruleMap[activityKind as keyof AttributeRuleMap]?.[
      eventKind as keyof AttributeRuleMap[keyof AttributeRuleMap]
      ] || []
    )
  }

  /**
   * Get default value for an attribute based on context
   *
   * Executes the defaultValue function if defined, otherwise returns null
   * Handles errors gracefully and logs them
   *
   * @param config - Attribute configuration
   * @param context - Current activity context
   * @returns Default value or null
   */
  getDefaultValue(config: AttributeConfig, context: ActivityContext): string | null {
    if (!config.defaultValue) {
      return null
    }

    try {
      return config.defaultValue(context)
    } catch (err) {
      console.error("Error calculating default value:", err)
      return null
    }
  }

  /**
   * Calculate an attribute value using calculator function
   *
   * Executes the calculator function if defined, otherwise returns null
   * Handles errors gracefully and logs them
   *
   * @param config - Attribute configuration
   * @param context - Current activity context
   * @returns Calculated value or null
   */
  async calculateValue(config: AttributeConfig, context: ActivityContext): Promise<string | null> {
    if (!config.calculator) {
      return null
    }

    try {
      return await config.calculator(context)
    } catch (err) {
      console.error("Error calculating value:", err)
      return null
    }
  }

  /**
   * Validate an attribute value
   *
   * Executes the validator function if defined, otherwise returns valid:true
   * Handles errors gracefully
   *
   * @param config - Attribute configuration
   * @param value - Value to validate
   * @param context - Current activity context
   * @returns Validation result with valid flag and optional error/warning
   */
  validateAttribute(config: AttributeConfig, value: string, context: ActivityContext): ValidationResult {
    if (!config.validator) {
      return { valid: true }
    }

    try {
      return config.validator(value, context)
    } catch (err) {
      console.error("Validation error:", err)
      return {
        valid: false,
        error: "Validation failed",
      }
    }
  }

  /**
   * Fetch IoT value via GraphQL query
   *
   * Executes the configured GraphQL query, transforms the result,
   * and returns the attribute value
   *
   * @param config - Attribute configuration with iotSource
   * @param context - Current activity context
   * @param apolloClient - Apollo Client instance for GraphQL queries
   * @returns Promise resolving to attribute value or fallback
   */
  async fetchIoTValue(
    config: AttributeConfig,
    context: ActivityContext,
    apolloClient: ApolloClient<any>,
    asset: AssetBasic | undefined,
    start: DateTime | undefined
  ): Promise<string | null> {
    if (!config.iotSource) {
      throw new Error("No IoT source configured")
    }

    const { query, variablesBuilder, transformer, fallbackValue } = config.iotSource

    try {
      // Build query variables from context
      let variables = variablesBuilder(context, asset, start)

      // Execute GraphQL query
      const { data, error } = await apolloClient.query({
        query,
        variables,
        fetchPolicy: "network-only", // Always fetch fresh IoT data
      })

      if (error) {
        console.error("IoT query error:", error)
        return fallbackValue ?? null
      }

      // Transform raw data into attribute value
      const value = transformer(data, context)
      return value
    } catch (err) {
      console.error("IoT fetch error:", err)
      return fallbackValue ?? null
    }
  }

  /**
   * Check if an attribute has IoT source configured
   *
   * @param config - Attribute configuration
   * @returns True if attribute is IoT-enabled with valid source
   */
  hasIoTSource(config: AttributeConfig): boolean {
    return config.visibility === "iot" && !!config.iotSource
  }
}
